/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";

import { colors, ExpandMoreIcon } from "@dotnet/telematics-design-system";

import SharedStatusBadge from "../../../../../components/SharedStatusBadge/SharedStatusBadge";
import Tooltip from "../../../../../components/Tooltip/Tooltip";
import { COMPANY_CARD_STATUS } from "../../../../../shared";
import { CCExpirationObjectDto } from "../../../types";
import useCCExpirationFunctions from "../hooks/useCCExpirationFunctions";
import { useStyles } from "../styles/styles";

interface CCExpirationAccordionItemProps {
  expandedPanel: string;
  title: string | ReactNode;
  index: number;
  onCollapse: (value: string, expanded: boolean) => void;
  expirydata: CCExpirationObjectDto[];
  isExpandable?: boolean;
}

const CCExpirationAccordionItem = ({
  expandedPanel,
  title,
  index,
  onCollapse,
  expirydata,
  isExpandable = true
}: CCExpirationAccordionItemProps) => {
  const { handleTakeMeThereClick } = useCCExpirationFunctions();
  const { t } = useTranslation();

  const isExpanded = expandedPanel === `panel${index}`;

  const {
    ccExpirySingleAccordionContainer,
    accordionItem,
    companyName,
    accordionItemTitle
  } = useStyles({
    isExpanded
  });

  return (
    <Accordion
      elevation={0}
      data-testid="ccExpiry-accordion"
      expanded={isExpanded}
      onChange={
        isExpandable
          ? (_, expanded) => onCollapse(`panel${index}`, expanded)
          : undefined
      }
      disableGutters
      css={css(ccExpirySingleAccordionContainer)}
    >
      <AccordionSummary
        data-testid="ccExpiry-accordion-summary"
        expandIcon={
          isExpandable ? (
            <ExpandMoreIcon data-testid="ExpandMoreOutlined" />
          ) : undefined
        }
        id="accordionHeader"
      >
        {title}
      </AccordionSummary>
      <AccordionDetails>
        {expirydata.map((item, index) => (
          <Tooltip
            key={item.cardId}
            data-testid={`status-company-list-item-tooltip-${item.companyName}`}
            title={t(`Dashboard##tooltip##take me there`)}
            placement="top"
            size="medium"
            hasArrow
          >
            <div
              onClick={() => handleTakeMeThereClick(item)}
              css={css(accordionItem)}
            >
              <span css={css(accordionItemTitle)}>
                <SharedStatusBadge
                  status={item.connectionStatus as COMPANY_CARD_STATUS}
                  bubbleOnly
                  showTooltip
                />
                <Typography variant="body2Medium" color={colors.textDark}>
                  {item.cardName}
                </Typography>
              </span>

              <Typography variant="body2Medium" sx={companyName as CSSObject}>
                {item.companyName}
              </Typography>
            </div>
          </Tooltip>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default CCExpirationAccordionItem;
